import {UI} from "../../../../../../stem-core/ui/UIBase";
import {StyleSheet} from "../../../../../../stem-core/ui/Style";

import {BlinkInput} from "../../../../../ui/Input.jsx";
import {Messages} from "../../../../../Messages.js";
import {AmazonPayLogo, AmExIcon, MasterCardIcon, VisaIcon} from "../../../../../../core/ui/SVGElements.jsx";
import {styleRule, styleRuleInherit} from "../../../../../../stem-core/decorators/Style";
import {
    StripeCardInput,
    StripeCardInputStyle
} from "../../../../../common/payment-methods/StripeCardInput.jsx";
import {registerStyle} from "../../../../../../stem-core/ui/style/Theme";
import {NOOP_FUNCTION} from "../../../../../Utils.js";
import {iFrameMerchantService} from "blinkpay/services/IframeMerchantService.js";
import {Button} from "../../../../../../stem-core/ui/button/Button.jsx";
import {AmazonPayWidget} from "../../../../../common/payment-methods/AmazonPayWidget.jsx";
import {PayPalWidget} from "../../../../../common/payment-methods/PayPalWidget.jsx";
import {LinkButton} from "../../../../../../core/ui/LinkButton.jsx";
import {PaymentProcessor, PaymentProcessorType} from "../../../../../../client/state/PaymentProcessorStore.js";
import {Theme} from "../../../../../../stem-core/ui/style/Theme";
import {Flow} from "../../../../flows/Flow.js";


class InlineErrorStripeCardInputStyle extends StripeCardInputStyle {
    @styleRuleInherit
    errorMessage = {
        position: "absolute",
        top: -6,
        paddingBottom: 0,
        left: 0,
        transform: "translateY(calc(-100%))",
        fontWeight: this.themeProps.FONT_WEIGHT_BOLD,
        fontSize: this.themeProps.FONT_SIZE_SMALL,
        marginTop: 0,
    };
}

class CardFormStyle extends StyleSheet {
    @styleRule
    cardIcons = {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: 10,
    };

    @styleRule
    cardIcon = {
        marginLeft: 8,
    };
}


@registerStyle(CardFormStyle)
export class CardForm extends UI.Element {
    getDefaultOptions() {
        return {
            onChange: NOOP_FUNCTION,
        }
    }

    updateAltPayData(provider, orderId) {
        Flow.paymentMethod.setAltPayOrderId(provider, orderId);
        this.options.onChange(); // This will redraw the parent component. TODO @cleanup bad pattern
    }

    getAltPaySection() {
        const altPayButtons = [];
        const paymentProcessorFilter = {
            merchantId: iFrameMerchantService.merchantId,
            isActive: true,
        };

        if (Flow.activeFlowHandler === Flow.donation) {
            paymentProcessorFilter.supportsDonations = true;
        }

        const amazonPaymentProcessor = PaymentProcessor.findBy({
            ...paymentProcessorFilter,
            provider: PaymentProcessorType.AMAZON,
        });

        const paypalPaymentProcessor = PaymentProcessor.findBy({
            ...paymentProcessorFilter,
            provider: PaymentProcessorType.PAYPAL,
        });

        if (amazonPaymentProcessor) {
            const useAmazonPay = () => AmazonPayWidget.requestWidgetInSDK(amazonPaymentProcessor, (amazonPayOrderId) => {
                this.updateAltPayData(PaymentProcessorType.AMAZON, amazonPayOrderId);
            });

            altPayButtons.push(
                <Button
                    style={{
                        width: "100%",
                        maxWidth: 350,
                        margin: "auto",
                        padding: 9,
                        borderRadius: 4,
                        borderColor: "black",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    onClick={useAmazonPay}>
                    <AmazonPayLogo style={{display: "inline-block", lineHeight: 0, marginTop: 2}}/>
                </Button>
            );
        }

        if (paypalPaymentProcessor) {
            const onApprove = (data) => {
                this.updateAltPayData(PaymentProcessorType.PAYPAL, data.billingToken);
            }

            altPayButtons.push(<PayPalWidget
                paymentProcessor={paypalPaymentProcessor}
                onApprove={onApprove}
                style={{width: "100%", maxWidth: 368, margin: "auto", paddingTop: 8}}
            />);
        }

        if (altPayButtons.length === 0) {
            return null;
        }

        return <div>
            <div style={{textAlign: "center"}}>
                {altPayButtons}
            </div>
            <div style={{textAlign: "center", paddingTop: 12}}>
                Or enter your card details below
            </div>
        </div>
    }

    render() {
        const {styleSheet} = this;
        const creditCardIconSize = 12;

        const CardInputClass = StripeCardInput;
        Theme.register(StripeCardInput, InlineErrorStripeCardInputStyle); // TODO A bit of a hack

        const {altPayData} = Flow.paymentMethod;

        if (altPayData) {
            // We've already got an alt payment selected, just needs to be confirmed
            return <div>
                You have selected {altPayData.provider.toString()} for this payment.
                <LinkButton
                    style={{marginTop: 8, display: "flex", textAlign: "center"}}
                    onClick={() => this.updateAltPayData(null)}
                    label="Use another payment method?"
                />
            </div>;
        }

        return [
            this.getAltPaySection(),

            <BlinkInput
                ref="nameInput"
                inputAttributes={{
                    placeholder: Messages.addCardNameInput.toString(),
                    autofocus: true
                }}
                initialValue={Flow.paymentMethod.cardHolderName}
                validators={[{
                    condition: () => !this.nameInput.isEmpty()
                }]}
                onBlur={() => this.nameInput.validate()}
                onChange={() => this.options.onChange()}
            />,
            <div className={styleSheet.cardIcons}>
                <VisaIcon size={creditCardIconSize} className={styleSheet.cardIcon}/>
                <MasterCardIcon size={creditCardIconSize} className={styleSheet.cardIcon}/>
                <AmExIcon size={creditCardIconSize} className={styleSheet.cardIcon}/>
            </div>,
            <CardInputClass
                ref="cardInput"
                autofocus={false}
                onChange={() => this.options.onChange()}
            />,
        ]
    }

    isValid() {
        if (Flow.paymentMethod.altPayData) {
            return true;
        }
        return this.cardInput?.isInputComplete() && this.nameInput?.isValid();
    }

    getExtraCardData() {
        let result = {};
        if (this.nameInput) {
            result.name = this.nameInput.getValue();
            Flow.paymentMethod.cardHolderName = result.name;
        }
        return result;
    }

    async prepareRequest(scope = Flow.paymentMethod.scope) {
        await this.cardInput?.prepareRequest(scope, this.getExtraCardData());
    }

    async confirmCard(scope = Flow.paymentMethod.scope) {
        if (this.cardInput) {
            return this.cardInput.confirmCard(scope, this.getExtraCardData());
        }

        // We're using an alt pay
        return {};
    }
}
